import { Link } from 'react-router-dom';
import { formatPrice } from '../lib/format';
import { resolveMediaUrl } from '../lib/media-url';
import type { ProductSummary } from '../lib/types';

function formatEstimatedMinutes(minutes: number) {
  if (minutes < 60) return `${minutes} phút`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours} giờ ${remainingMinutes} phút` : `${hours} giờ`;
}

/**
 * Two shapes from variant 5a of the design:
 *  - a course is a raised white card with a 200px cover,
 *  - a skill is a flat tinted block with a shorter cover and a "Chi tiết" link.
 *
 * Course kickers use lessonCount and estimatedMinutes from the catalog API.
 * Missing values fall back to the product type rather than invented numbers.
 */
export function ProductCard({ product }: { product: ProductSummary }) {
  const isSkill = product.type === 'SKILL';
  const href = `/san-pham/${product.slug}`;
  const typeLabel = isSkill ? 'Skill tải về' : 'Khóa học';
  const courseFacts = isSkill
    ? []
    : [
        product.lessonCount && product.lessonCount > 0 ? `${product.lessonCount} bài` : null,
        product.estimatedMinutes && product.estimatedMinutes > 0
          ? formatEstimatedMinutes(product.estimatedMinutes)
          : null,
      ].filter((fact): fact is string => fact !== null);

  return (
    <article className={isSkill ? 'card card-tint' : 'card'}>
      <Link to={href} className="card-media" tabIndex={-1} aria-hidden="true">
        {product.thumbnailUrl ? (
          <img src={resolveMediaUrl(product.thumbnailUrl)} alt="" loading="lazy" className="card-thumb" />
        ) : (
          <span className="card-media-placeholder">
            <span>{isSkill ? 'S' : 'AI'}</span>
          </span>
        )}
      </Link>

      <div className="card-body">
        <span className="card-kicker">
          {courseFacts.length > 0 ? courseFacts.join(' · ') : typeLabel}
        </span>
        <h3 className="card-title">
          <Link to={href}>{product.title}</Link>
        </h3>

        {product.shortDescription && <p className="card-text">{product.shortDescription}</p>}

        <div className="card-foot">
          <span className="price">{formatPrice(product.price)}</span>
          {isSkill ? (
            <Link to={href} className="link-accent" aria-label={`Xem chi tiết ${product.title}`}>
              Chi tiết
            </Link>
          ) : (
            <Link to={href} className="btn btn-small" aria-label={`Xem khóa học ${product.title}`}>
              Xem khóa học
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
